/** @format */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

/**
 * Payroll Dashboard with Setup Checklist
 * Created: October 30, 2025
 * Purpose: Overview of payroll system status and quick actions
 */
const PayrollDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    employeesWithTemplates: 0,
    payHeadsCount: 0,
    templatesCount: 0,
    payslipsThisMonth: 0,
    pendingPayslips: 0,
  });
  const [setupStatus, setSetupStatus] = useState({
    payrollConfig: false,
    payHeads: false,
    payTemplates: false,
    employeeAssignments: false,
  });
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch only working endpoints - use existing API structure
      const employeesRes = await axios.get("/employee/getAllEmployees").catch(() => ({ data: [] }));
      const configRes = await axios.get("/payroll/payrollConfig").catch(() => ({ data: null }));

      const employees = employeesRes.data || [];
      
      // Mock data for now since backend endpoints don't exist yet
      const payHeads = [];
      const templates = [];
      const payslips = [];

      // Calculate stats
      const employeesWithTemplates = employees.filter(emp => emp.payTemplateId).length;
      const currentMonth = new Date().getMonth();
      const payslipsThisMonth = payslips.filter(p => {
        const pDate = new Date(p.fromDate);
        return pDate.getMonth() === currentMonth;
      }).length;

      setStats({
        totalEmployees: employees.length,
        employeesWithTemplates,
        payHeadsCount: payHeads.length,
        templatesCount: templates.length,
        payslipsThisMonth,
        pendingPayslips: payslips.filter(p => p.status === "DRAFT").length,
      });

      // Check setup status
      setSetupStatus({
        payrollConfig: configRes.data && configRes.data.length > 0,
        payHeads: payHeads.length > 0,
        payTemplates: templates.length > 0,
        employeeAssignments: employeesWithTemplates > 0,
      });

      // Generate alerts
      const newAlerts = [];
      if (employees.length > 0 && employeesWithTemplates === 0) {
        newAlerts.push({
          severity: "error",
          message: "No employees have pay templates assigned. Assign templates to start generating payslips.",
          action: () => navigate("/userdashboard/staff/hrm/manageEmployee"),
          actionLabel: "Assign Templates",
        });
      } else if (employeesWithTemplates < employees.length) {
        newAlerts.push({
          severity: "warning",
          message: `${employees.length - employeesWithTemplates} employees don't have pay templates assigned.`,
          action: () => navigate("/userdashboard/staff/hrm/manageEmployee"),
          actionLabel: "View Employees",
        });
      }

      if (templates.length === 0) {
        newAlerts.push({
          severity: "error",
          message: "No pay templates created. Create templates to define salary structures.",
          action: () => navigate("/userdashboard/payroll/configuration/PayTemplatePayroll"),
          actionLabel: "Create Templates",
        });
      }

      if (payHeads.length === 0) {
        newAlerts.push({
          severity: "error",
          message: "No pay heads created. Create pay heads (Basic, HRA, PF, etc.) first.",
          action: () => navigate("/userdashboard/payroll/configuration/PayrollPayHead"),
          actionLabel: "Create Pay Heads",
        });
      }

      setAlerts(newAlerts);

    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getSetupProgress = () => {
    const completed = Object.values(setupStatus).filter(Boolean).length;
    return (completed / 4) * 100;
  };

  const isSetupComplete = () => {
    return Object.values(setupStatus).every(Boolean);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Payroll Dashboard
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payroll Dashboard</Typography>
        {!isSetupComplete() && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/userdashboard/payroll/setup-wizard")}
          >
            Setup Wizard
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Box mb={3}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.severity}
              sx={{ mb: 1 }}
              action={
                alert.action && (
                  <Button color="inherit" size="small" onClick={alert.action}>
                    {alert.actionLabel}
                  </Button>
                )
              }
            >
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Setup Progress */}
      {!isSetupComplete() && (
        <Card sx={{ mb: 3, bgcolor: "primary.light", color: "primary.contrastText" }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Complete Your Setup</Typography>
              <Chip
                label={`${Math.round(getSetupProgress())}% Complete`}
                color="default"
                sx={{ bgcolor: "white" }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={getSetupProgress()}
              sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.3)" }}
            />
            <List dense sx={{ mt: 2 }}>
              <ListItem>
                <ListItemIcon>
                  {setupStatus.payHeads ? (
                    <CheckCircleIcon sx={{ color: "white" }} />
                  ) : (
                    <WarningIcon sx={{ color: "white" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Create Pay Heads"
                  secondary={
                    setupStatus.payHeads
                      ? `${stats.payHeadsCount} pay heads created`
                      : "Define salary components"
                  }
                  secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
                />
                {!setupStatus.payHeads && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ color: "white", borderColor: "white" }}
                    onClick={() => navigate("/userdashboard/payroll/configuration/PayrollPayHead")}
                  >
                    Start
                  </Button>
                )}
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {setupStatus.payTemplates ? (
                    <CheckCircleIcon sx={{ color: "white" }} />
                  ) : (
                    <WarningIcon sx={{ color: "white" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Create Pay Templates"
                  secondary={
                    setupStatus.payTemplates
                      ? `${stats.templatesCount} templates created`
                      : "Build salary structures"
                  }
                  secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
                />
                {!setupStatus.payTemplates && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ color: "white", borderColor: "white" }}
                    onClick={() => navigate("/userdashboard/payroll/configuration/PayTemplatePayroll")}
                  >
                    Start
                  </Button>
                )}
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {setupStatus.employeeAssignments ? (
                    <CheckCircleIcon sx={{ color: "white" }} />
                  ) : (
                    <WarningIcon sx={{ color: "white" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Assign Templates to Employees"
                  secondary={
                    setupStatus.employeeAssignments
                      ? `${stats.employeesWithTemplates}/${stats.totalEmployees} employees assigned`
                      : "Link employees to salary structures"
                  }
                  secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
                />
                {!setupStatus.employeeAssignments && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ color: "white", borderColor: "white" }}
                    onClick={() => navigate("/userdashboard/staff/hrm/manageEmployee")}
                  >
                    Start
                  </Button>
                )}
              </ListItem>
            </List>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Employees
                  </Typography>
                  <Typography variant="h4">{stats.totalEmployees}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, color: "primary.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    With Templates
                  </Typography>
                  <Typography variant="h4">{stats.employeesWithTemplates}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {stats.totalEmployees}
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 48, color: "success.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Payslips This Month
                  </Typography>
                  <Typography variant="h4">{stats.payslipsThisMonth}</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, color: "info.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pending Payslips
                  </Typography>
                  <Typography variant="h4">{stats.pendingPayslips}</Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 48, color: "warning.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PlayArrowIcon />}
                onClick={() => navigate("/userdashboard/payroll/paySlip/create")}
                disabled={stats.employeesWithTemplates === 0}
              >
                Compute Payslip
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PlayArrowIcon />}
                onClick={() => navigate("/userdashboard/payroll/batchPayslip/create")}
                disabled={stats.employeesWithTemplates === 0}
              >
                Batch Payslip
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<InfoIcon />}
                onClick={() => navigate("/userdashboard/payroll/paySlip")}
              >
                View Payslips
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<InfoIcon />}
                onClick={() => navigate("/userdashboard/payroll/configuration/PayTemplatePayroll")}
              >
                Manage Templates
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Help Section */}
      {!isSetupComplete() && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Need Help Getting Started?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Follow our step-by-step setup wizard to configure your payroll system in minutes.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/userdashboard/payroll/setup-wizard")}
            >
              Launch Setup Wizard
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PayrollDashboard;
